import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import { useAppDispatch, useAppSelector, type User } from '@verantos-dev/core-annotation-tool';

import { useGetSupervisorsQuery } from '../../../create-project-wizard/api/createProjectApiSlice';
import { useGetProjectDetailsQuery } from '../../api/adminProjectsApiSlice';
import { addSupervisor, removeSupervisor, setSupervisors } from '../../state/adminProjectSlice';
import { selectSupervisors } from '../../state/selector';

interface UseSupervisorSelectionResults {
  supervisors: User[];
  selectedSupervisors: User[];
  handleAddSupervisor: (supervisor: User) => void;
  handleRemoveSupervisor: (supervisor: User) => void;
  supervisorInputValue: string;
  lastTypedSupervisorValue: React.MutableRefObject<string>;
  setSupervisorInputValue: (value: string) => void;
  isSupervisorsLoading: boolean;
}

export const useSupervisorSelection = (): UseSupervisorSelectionResults => {
  const {
    data: supervisorsData,
    isLoading: isSupervisorsLoading,
    isFetching: isSupervisorsFetching,
  } = useGetSupervisorsQuery();
  const allSupervisors: User[] = useMemo(() => supervisorsData?.data ?? [], [supervisorsData]);

  const { projectId } = useParams();
  const {
    data: projectDetailsData,
    isLoading: isProjectDetailsLoading,
    isFetching: isProjectDetailsFetching,
  } = useGetProjectDetailsQuery({ projectId: projectId as string });

  const projectSupervisors: User[] = useMemo(
    () => projectDetailsData?.data?.supervisors ?? [],
    [projectDetailsData]
  );

  const dispatch = useAppDispatch();
  useEffect(() => {
    dispatch(setSupervisors(projectSupervisors));
  }, [projectSupervisors]);

  const selectedSupervisors = useAppSelector(selectSupervisors);

  const handleAddSupervisor = (supervisor: User) => {
    dispatch(addSupervisor(supervisor));
  };
  const handleRemoveSupervisor = (supervisor: User) => {
    dispatch(removeSupervisor(supervisor.id));
  };
  const [supervisorInputValue, setSupervisorInputValue] = useState<string>('');

  const lastTypedSupervisorValue = useRef<string>('');
  return {
    selectedSupervisors: selectedSupervisors || [],
    handleRemoveSupervisor,
    supervisors: allSupervisors,
    supervisorInputValue,
    lastTypedSupervisorValue,
    setSupervisorInputValue,
    handleAddSupervisor,
    isSupervisorsLoading: isSupervisorsLoading || isSupervisorsFetching,
  };
};
